import { getShapeById } from '~/composables/shapes/utils'

export type Point = { x: number; y: number }

export type FootprintTile = {
  shapeId?: string
  x: number
  y: number
  rotation?: number
  width?: number
  height?: number
}

const DEFAULT_SIDE = 120
const ARC_STEPS = 24
// Matches haveIntersection: tiles that only touch, or share an edge within a pixel, do not overlap.
const TOUCH_TOLERANCE = 1

const turned = (points: Point[], rotation: number): Point[] => {
  const rad = (rotation * Math.PI) / 180
  const cos = Math.cos(rad)
  const sin = Math.sin(rad)
  return points.map(p => ({ x: p.x * cos - p.y * sin, y: p.x * sin + p.y * cos }))
}

/**
 * The convex polygon a tile covers on the canvas, in the same geometry the shapes are drawn with.
 * Rects swap their sides on a quarter turn, triangles, diamonds and hexes turn about their center,
 * and an arc is a wedge around its circle center (x, y).
 */
export const tileFootprint = ({ shapeId, x, y, rotation = 0, width, height }: FootprintTile): Point[] => {
  const def = shapeId ? getShapeById(shapeId) : undefined
  const w = def?.w ?? width ?? DEFAULT_SIDE
  const h = def?.h ?? height ?? DEFAULT_SIDE
  let local: Point[]

  switch (def?.type) {
    case 'rect-triangle':
      local = turned([{ x: -w / 2, y: -w / 2 }, { x: w / 2, y: -w / 2 }, { x: w / 2, y: w / 2 }], rotation)
      break
    case 'diamond':
      local = turned([{ x: -w / 2, y: 0 }, { x: 0, y: -h / 2 }, { x: w / 2, y: 0 }, { x: 0, y: h / 2 }], rotation)
      break
    case 'triangle':
      local = turned([{ x: -w / 2, y: h / 2 }, { x: 0, y: -h / 2 }, { x: w / 2, y: h / 2 }], rotation)
      break
    case 'hex': {
      const sides = def.sides ?? 6
      const radius = def.radius ?? Math.min(w, h) / 2
      const ring = Array.from({ length: sides }, (_, i) => {
        const angle = (2 * Math.PI * i) / sides - Math.PI / 2
        return { x: radius * Math.cos(angle), y: radius * Math.sin(angle) }
      })
      local = turned(ring, rotation)
      break
    }
    case 'arc': {
      // shortcut: wedges wider than 180 degrees are not convex, none exist in the catalogs. Split them if one is added.
      const sweep = def.angle ?? 360
      const radius = def.radius ?? Math.max(w, h) / 2
      const ring = Array.from({ length: ARC_STEPS + 1 }, (_, i) => {
        const angle = ((rotation + (sweep * i) / ARC_STEPS) * Math.PI) / 180
        return { x: radius * Math.cos(angle), y: radius * Math.sin(angle) }
      })
      local = sweep >= 360 ? ring.slice(0, -1) : [{ x: 0, y: 0 }, ...ring]
      break
    }
    default: {
      const quarter = Math.abs(rotation % 180) === 90
      const hw = (quarter ? h : w) / 2
      const hh = (quarter ? w : h) / 2
      local = [{ x: -hw, y: -hh }, { x: hw, y: -hh }, { x: hw, y: hh }, { x: -hw, y: hh }]
    }
  }

  return local.map(p => ({ x: x + p.x, y: y + p.y }))
}

const extent = (polygon: Point[], axis: Point) => {
  const dots = polygon.map(p => p.x * axis.x + p.y * axis.y)
  return { min: Math.min(...dots), max: Math.max(...dots) }
}

// Separating axis test for convex polygons. They overlap when no edge normal separates them by more than the tolerance.
export const footprintsOverlap = (a: Point[], b: Point[], tolerance = TOUCH_TOLERANCE): boolean => {
  for (const polygon of [a, b]) {
    for (let i = 0; i < polygon.length; i++) {
      const p = polygon[i]
      const q = polygon[(i + 1) % polygon.length]
      const length = Math.hypot(q.x - p.x, q.y - p.y)
      if (length === 0) continue
      const axis = { x: (q.y - p.y) / length, y: (p.x - q.x) / length }
      const ea = extent(a, axis)
      const eb = extent(b, axis)
      if (Math.min(ea.max, eb.max) - Math.max(ea.min, eb.min) <= tolerance) return false
    }
  }
  return true
}
